import { Editor } from '@tinymce/tinymce-react';
import React, { useRef } from 'react';

import { $ } from '../../../base/bootstrap';
import { isPremierDubai } from '../../../utils/template';
import { addGoogleMapButton, processNewBlock, processNodeChange } from '../../helpers/tinymce';
import { loadFileManagerToIframe } from '../../pages/fileManager/FileManager';

// editor: any is intentional due to the excessive number of packages in node_modules.
// Using the Editor causes issues and there is no workaround for these errors.
type Props = {
    id: string;
    value: string;
    onEditorChange: (a: string, editor: any) => void;
    disabled?: boolean;
};

function TextEditor({ id, value, onEditorChange, disabled = false }: Props) {
    const activeEditor = useRef<any>();

    return (
        <Editor
            tinymceScriptSrc={`${window.location.origin}/js/crm/tinymce/tinymce.min.js`}
            id={id}
            toolbar={`insertfile undo redo | bold italic underline strikethrough
                | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify
                | outdent indent | numlist bullist | forecolor backcolor removeformat | pagebreak
                | charmap emoticons | fullscreen preview save print | image template link anchor codesample
                | ltr rtl | google_map`}
            init={{
                height: 400,
                relative_urls: false,
                image_advtab: true,
                license_key: 'gpl',
                file_picker_callback: (callback) => {
                    const x =
                        window.innerWidth ||
                        document.documentElement.clientWidth ||
                        document.querySelector('body').clientWidth;
                    const y =
                        window.innerHeight ||
                        document.documentElement.clientHeight ||
                        document.querySelector('body').clientHeight;

                    activeEditor.current.windowManager.openUrl({
                        url: 'about:blank',
                        title: 'Filemanager',
                        width: x * 0.8,
                        height: y * 0.8,
                    });
                    const iframe = $('iframe').filter("[src='about:blank']").get(0) as HTMLIFrameElement;

                    if (iframe) {
                        loadFileManagerToIframe(iframe, () => {
                            // eslint-disable-next-line dot-notation
                            iframe.contentWindow['secured_filemanager_field_callback'] = callback;
                        });
                    }
                },
                setup: (editor) => {
                    activeEditor.current = editor;

                    editor.on('BeforeSetContent', (e) => {
                        // wrap selected elements with a bootstrap responsive class
                        if (e.content.startsWith('<table')) {
                            e.content = `<div class='table-responsive'>${e.content}</div>`;
                        }

                        if (e.content.startsWith('<iframe')) {
                            e.content = `<div class='embed-responsive embed-responsive-16by9'>${e.content}</div>`;
                        }
                    });

                    editor.on('NodeChange', (event) => processNodeChange(event.element, activeEditor.current));

                    editor.on('NewBlock', (event) => processNewBlock(event.newBlock, activeEditor.current));

                    if (isPremierDubai()) {
                        addGoogleMapButton(activeEditor.current);
                    }
                },
                extended_valid_elements: 'table[class=table]',
            }}
            plugins={`print preview paste importcss searchreplace autolink autosave save directionality
             code visualblocks visualchars fullscreen image link media codesample table charmap hr
             pagebreak nonbreaking anchor insertdatetime advlist lists wordcount imagetools textpattern
             noneditable help charmap emoticons`}
            value={value}
            onEditorChange={onEditorChange}
            disabled={disabled}
        />
    );
}

export default TextEditor;
